/** @jsx jsx */
/** @jsxFrag React.Fragment */

import React, { ReactNode, useEffect, useReducer } from 'react';

import {
  Grid,
  ModalDialogHeader,
  ModalDialogBody,
  ModalDialogFooter,
  Text,
} from '@zapier/design-system';
import { Spacing } from '@zapier/theme';
import { MessageBar } from '@zapier/common-components';
import { css, jsx } from '@emotion/core';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import ServiceIcon from 'app/developer-v3/components/ServiceIcon';
import { ROLES } from 'app/developer-v3/constants';

import HeroImage from './hero-image.svg';
import SectionJobTitle from './SectionJobTitle';
import SectionName from './SectionName';
import SectionOptIn from './SectionOptIn';
import SectionRole from './SectionRole';
import SectionTos from './SectionTos';

import type { PartnerProfileFormValues, Role, Sections } from './types';

const Styles = {
  integrationImageContainer: css`
    position: relative;
    padding-bottom: ${Spacing.medium}px;
  `,
  integrationImageWelcome: css`
    width: 100%;
  `,
};

type Props = {
  appImage?: string;
  appTitle?: string;
  errorMessages: string[];
  firstName?: string;
  formTitle: string;
  formDescription?: ReactNode;
  invitationType?: ROLES.admin | ROLES.collaborator;
  isHeroImageVisible: boolean;
  isSaving: boolean;
  jobTitle: string;
  lastName?: string;
  onConfirm: (form: PartnerProfileFormValues) => void;
  roles: Array<Role>;
  sections: Array<Sections>;
  submitText: string;
  termsAccepted?: boolean;
};

function PartnerProfileForm({
  appImage,
  appTitle,
  errorMessages,
  firstName = '',
  formTitle,
  formDescription,
  invitationType,
  isHeroImageVisible,
  isSaving,
  jobTitle = '',
  lastName = '',
  onConfirm,
  roles = [],
  sections,
  submitText,
  termsAccepted,
}: Props) {
  const [state, setState] = useReducer(
    (currentState, nextState) => ({ ...currentState, ...nextState }),
    {
      firstName,
      jobTitle,
      lastName,
      optIn: true,
      roles,
      tos: false,
    }
  );

  useEffect(() => {
    setState({ firstName, jobTitle, lastName, roles });
  }, [firstName, jobTitle, lastName, roles]);

  const areRolesValid = () => state.roles.some(role => role.checked);

  const hasMissingField = () => {
    return (
      (sections.includes('name') &&
        (!state.firstName.length || !state.lastName.length)) ||
      (sections.includes('roles') && !areRolesValid()) ||
      (sections.includes('tos') && !termsAccepted && !state.tos)
    );
  };

  const handleRoleCheckboxChange = checkboxValue => () => {
    const rolesNext = state.roles.map(role => ({
      ...role,
      checked: role.value === checkboxValue ? !role.checked : role.checked,
    }));

    setState({ roles: rolesNext });
  };

  const handleCheckBox = key => () => {
    const current = state[key];
    setState({ [key]: !current });
  };

  return (
    <>
      {isHeroImageVisible && (
        <div css={Styles.integrationImageContainer}>
          <div aria-label="Banner" css={Styles.integrationImageWelcome}>
            <HeroImage alt="Welcome to Zapier!" title="Welcome to Zapier" />
          </div>
        </div>
      )}
      <ModalDialogHeader>
        <Grid columns={appImage && appTitle ? '60px 1fr' : 1} gap={40}>
          {appImage && appTitle && (
            <Grid>
              <ServiceIcon
                isSymbol={false}
                serviceName={appTitle}
                size={60}
                src={appImage}
              />
            </Grid>
          )}
          <Grid>
            <Text
              fontWeight={700}
              tag="h1"
              textAlign="center"
              type="SectionHeader"
            >
              {formTitle}
            </Text>
          </Grid>
        </Grid>
        {formDescription && (
          <Text margin="30px 0 0 0" tag="p" type="Body1">
            {formDescription}
          </Text>
        )}
      </ModalDialogHeader>
      <ModalDialogBody>
        <section>
          {errorMessages &&
            errorMessages.map((message, index) => (
              <MessageBar key={index} type="error">
                {message}
              </MessageBar>
            ))}
          {sections.includes('name') && (
            <SectionName
              firstName={state.firstName}
              lastName={state.lastName}
              onChangeFirstName={firstNameNext =>
                setState({ firstName: firstNameNext })
              }
              onChangeLastName={lastNameNext =>
                setState({ lastName: lastNameNext })
              }
            />
          )}
          {sections.includes('roles') && (
            <SectionRole
              handleRoleCheckboxChange={handleRoleCheckboxChange}
              roles={state.roles}
            />
          )}
          {sections.includes('title') && (
            <SectionJobTitle
              jobTitle={state.jobTitle}
              onChange={jobTitleNext => setState({ jobTitle: jobTitleNext })}
            />
          )}
          {sections.includes('opt-in') && (
            <SectionOptIn handleCheckBox={handleCheckBox} optIn={state.optIn} />
          )}
          {sections.includes('tos') && !termsAccepted && (
            <SectionTos handleCheckBox={handleCheckBox} tos={state.tos} />
          )}
        </section>
      </ModalDialogBody>
      <ModalDialogFooter>
        <AsyncButton
          aria-describedby="animation"
          isDisabled={isSaving || hasMissingField()}
          isWorking={isSaving}
          onPress={() =>
            onConfirm({
              firstName: state.firstName,
              ...(invitationType ? { invitationType } : {}),
              jobTitle: state.jobTitle,
              lastName: state.lastName,
              optIn: state.optIn,
              roles: state.roles,
              tos: state.tos,
            })
          }
        >
          {submitText}
        </AsyncButton>
      </ModalDialogFooter>
    </>
  );
}

export default PartnerProfileForm;
